import axios from "axios";
import Swal from "sweetalert2";

export const safeValue = (value, defaultValue = "") => {
  if (value === null || value === undefined || value === "") {
    return defaultValue;
  }
  // ✅ Convertir a string para comparaciones consistentes
  return value.toString();
};

// ✅ Redondea un peso a 2 decimales para precargar en inputs editables (sin comas).
// A diferencia de formatWeight, no agrega separador de miles porque rompería
// inputs type="number", y devuelve "" en vez de "N/A" para no invalidar el input.
export const roundWeightInput = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const numericValue =
    typeof value === "string" ? Number(value.replace(/,/g, "")) : Number(value);
  if (Number.isNaN(numericValue)) return "";

  return numericValue.toFixed(2);
};

// ✅ Formatea peso con 2 decimales fijos y separador de miles. Ej: 1,548.00
export const formatWeight = (value, defaultValue = "N/A") => {
  if (value === null || value === undefined || value === "" || value === "-") {
    return defaultValue;
  }
  const numericValue =
    typeof value === "string" ? Number(value.replace(/,/g, "")) : Number(value);
  if (Number.isNaN(numericValue)) return defaultValue;

  return numericValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// ✅ Formatea volumen con 5 decimales fijos y separador de miles. Ej: 9.00000
export const formatVolume = (value, defaultValue = "N/A") => {
  if (value === null || value === undefined || value === "" || value === "-") {
    return defaultValue;
  }
  const numericValue =
    typeof value === "string" ? Number(value.replace(/,/g, "")) : Number(value);
  if (Number.isNaN(numericValue)) return defaultValue;

  return numericValue.toLocaleString("en-US", {
    minimumFractionDigits: 5,
    maximumFractionDigits: 5,
  });
};

// ✅ Formatea porcentaje (máximo 2 decimales)
export const formatPercentage = (value, defaultValue = "N/A") => {
  if (value === null || value === undefined || value === "" || value === "-") {
    return defaultValue;
  }
  const numericValue =
    typeof value === "string" ? Number(value.replace(/,/g, "")) : Number(value);
  if (Number.isNaN(numericValue)) return defaultValue;

  return parseFloat(numericValue.toFixed(2)).toString();
};

const MEXICO_TZ = "America/Mexico_City";
const pad2 = (n) => String(n).padStart(2, "0");

const buildMexicoDateTime = (year, month, day, hours, minutes, seconds) => {
  const period = hours >= 12 ? "p" : "a";
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${pad2(day)}/${pad2(month)}/${year}, ${pad2(hour12)}:${pad2(minutes)}:${pad2(seconds)} ${period}. m.`;
};

// ✅ Homologa fecha y hora en zona horaria de Ciudad de México.
// Formato: DD/MM/AAAA, hh:mm:ss a. m./p. m.
// - Si recibe una fecha "naive" (sin zona horaria, ej. "2026-07-13 08:39:17"),
//   se asume que ya representa la hora local de Ciudad de México y solo se reformatea.
// - Si recibe un Date, timestamp o string con zona horaria, se convierte a la
//   zona horaria de Ciudad de México.
export const formatMexicoDateTime = (input) => {
  if (typeof input === "string") {
    const naive = input
      .trim()
      .match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
    if (naive) {
      const [year, month, day, hours, minutes, seconds] = naive
        .slice(1)
        .map(Number);
      return buildMexicoDateTime(year, month, day, hours, minutes, seconds);
    }
  }

  const date = input ? new Date(input) : new Date();
  if (Number.isNaN(date.getTime())) {
    return typeof input === "string" ? input : "";
  }

  const formatted = date.toLocaleString("es-MX", {
    timeZone: MEXICO_TZ,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

  return formatted.replace(/([ap])\.\s*m\./i, "$1. m.");
};

// ✅ Nueva función para valores de radio buttons
export const safeRadioValue = (value, defaultValue = "1") => {
  if (value === null || value === undefined || value === "") {
    return defaultValue;
  }
  // Asegurar que sea string para los radio buttons
  return value.toString();
};

// Función para verificar si un valor existe (no null, undefined, o string vacío)
export const hasValue = (value) => {
  return value !== null && value !== undefined && value !== "";
};

// Función para verificar checkboxes basados en valores existentes
export const isChecked = (value) => {
  return hasValue(value) && value !== "0" && value !== "false";
};

// ✅ Validar que la suma no supere los valores máximos
export const validateSums = (formData, master, side = "left") => {
  let totalPieces = 0;
  let totalWeight = 0;

  if (side === "left") {
    // Validación lado izquierdo (naturaleza)
    const leftFields = [
      "nat_cgo",
      "nat_dgr",
      "nat_per",
      "nat_avi",
      "nat_val",
      "nat_hum",
      "nat_dip",
      "nat_nwp",
      "nat_mal",
      "nat_com",
      "nat_vic",
      "nat_pil",
    ];

    leftFields.forEach((field) => {
      if (formData[field]) {
        totalPieces += Number(formData[`${field}_pieces`]) || 0;
        totalWeight += Number(formData[`${field}_weight`]) || 0;
      }
    });

    // Obtener valores máximos del master
    const maxPieces = Number(master?.resp?.master_pieces) || 0;
    const maxWeight = Number(master?.resp?.master_weight) || 0;

    return {
      isValid: totalPieces <= maxPieces && totalWeight <= maxWeight,
      totalPieces,
      totalWeight,
      maxPieces,
      maxWeight,
      errors: {
        pieces:
          totalPieces > maxPieces
            ? `Piezas exceden máximo (${maxPieces})`
            : null,
        weight:
          totalWeight > maxWeight ? `Peso excede máximo (${maxWeight})` : null,
      },
    };
  } else {
    // Validación lado derecho (manejo)
    const rightFields = [
      "han_ice",
      "han_wet",
      "han_big",
      "han_hea",
      "han_hwc",
      "han_col",
      "han_fro",
      "han_na",
    ];

    rightFields.forEach((field) => {
      if (formData[field]) {
        totalPieces += Number(formData[`${field}_pieces`]) || 0;
        totalWeight += Number(formData[`${field}_weight`]) || 0;
      }
    });

    const maxPieces = Number(master?.resp?.master_pieces) || 0;
    const maxWeight = Number(master?.resp?.master_weight) || 0;

    return {
      isValid: totalPieces <= maxPieces && totalWeight <= maxWeight,
      totalPieces,
      totalWeight,
      maxPieces,
      maxWeight,
      errors: {
        pieces:
          totalPieces > maxPieces
            ? `Piezas exceden máximo (${maxPieces})`
            : null,
        weight:
          totalWeight > maxWeight ? `Peso excede máximo (${maxWeight})` : null,
      },
    };
  }
};

// ✅ Validar que si N/A está seleccionado, no haya otros seleccionados
export const validateExclusiveNA = (formData) => {
  const handleFields = [
    "han_ice",
    "han_wet",
    "han_big",
    "han_hea",
    "han_hwc",
    "han_col",
    "han_fro",
  ];
  const isNASelected = formData.han_na;

  if (isNASelected) {
    // Si N/A está seleccionado, verificar que no haya otros seleccionados
    const otherSelected = handleFields.some((field) => formData[field]);
    return !otherSelected;
  }
  return true;
};

// ✅ Obtener valores máximos para mostrar en mensajes
export const getMaxValues = (master) => {
  return {
    pieces: Number(master?.resp?.master_pieces) || 0,
    weight: Number(master?.resp?.master_weight) || 0,
  };
};

// ✅ Función para validar vuelos vs REFRI/CONGELADOR
export const validateFlightSums = (formData, master, type = "col") => {
  if (!master?.booking_flights) return null;

  const fieldPieces = type === "col" ? "han_col_pieces" : "han_fro_pieces";
  const fieldWeight = type === "col" ? "han_col_weight" : "han_fro_weight";

  const totalFromField = Number(formData[fieldPieces]) || 0;
  const weightFromField = Number(formData[fieldWeight]) || 0;

  let totalFlightPieces = 0;
  let totalFlightWeight = 0;

  master.booking_flights.forEach((_, index) => {
    totalFlightPieces +=
      Number(formData[`flights_${type}_pieces_${index}`]) || 0;
    totalFlightWeight +=
      Number(formData[`flights_${type}_weight_${index}`]) || 0;
  });

  if (
    totalFromField !== totalFlightPieces ||
    weightFromField !== totalFlightWeight
  ) {
    return {
      isValid: false,
      fieldTotal: { pieces: totalFromField, weight: weightFromField },
      flightTotal: { pieces: totalFlightPieces, weight: totalFlightWeight },
      message: `La suma de vuelos ${
        type === "col" ? "REFRI" : "CONGELADOR"
      } (${totalFlightPieces}pz/${totalFlightWeight}kg) no coincide con ${
        type === "col" ? "REFRI" : "CONGELADOR"
      } (${totalFromField}pz/${weightFromField}kg)`,
    };
  }

  return {
    isValid: true,
    fieldTotal: { pieces: totalFromField, weight: weightFromField },
    flightTotal: { pieces: totalFlightPieces, weight: totalFlightWeight },
  };
};

export const saveReceptionData = async (formData, master, subproduct, user) => {
  try {
    console.log("📤 Iniciando envío de datos al backend...");
    console.log("📊 FormData recibido:", formData);

    // ✅ Validaciones previas
    const leftResult = validateSums(formData, master, "left");
    const rightResult = validateSums(formData, master, "right");
    const naValid = validateExclusiveNA(formData);

    if (!leftResult.isValid) {
      throw new Error(leftResult.errors.pieces || leftResult.errors.weight);
    }

    if (!rightResult.isValid) {
      throw new Error(rightResult.errors.pieces || rightResult.errors.weight);
    }

    if (!naValid) {
      throw new Error(
        "Si N/A está seleccionado, no puede haber otros tipos de manejo seleccionados"
      );
    }

    // Validar vuelos
    if (formData.han_col) {
      const colValidation = validateFlightSums(formData, master, "col");
      if (colValidation && !colValidation.isValid) {
        throw new Error(colValidation.message);
      }
    }

    if (formData.han_fro) {
      const froValidation = validateFlightSums(formData, master, "fro");
      if (froValidation && !froValidation.isValid) {
        throw new Error(froValidation.message);
      }
    }

    const getUserId = () => {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      return user?.id || "";
    };

    // ✅ CORREGIDO: Usar números en lugar de strings
    const payload = {
      id_master: master.resp?.master_id || master.master?.id,
      id_house: subproduct?.id || null,
      user_id: getUserId(),
      preve_save: master.preve_save?.id || null,

      // ✅ CORREGIDO: Usar números para campos booleanos
      master_awb: (formData.master_awb === "1" || formData.master_awb === 1) ? 1 : 0,
      master_awb_e: formData.master_eawb ? 1 : 0,
      house_manifest: subproduct
        ? (formData.house_manifest === "1" || formData.house_manifest === 1
            ? 1
            : 0)
        : null,
      simplified_request:
        formData.simplified_request === "1" || formData.simplified_request === 1
          ? 1
          : 0,
      cargo_transfer:
        formData.cargo_transfer === "1" || formData.cargo_transfer === 1
          ? 1
          : 0,
      security_letter:
        formData.security_letter === "1" || formData.security_letter === 1
          ? 1
          : 0,
      acceptance_letter:
        formData.acceptance_letter === "1" || formData.acceptance_letter === 1
          ? 1
          : 0,
      custom_badge:
        formData.custom_badge === "1" || formData.custom_badge === 1
          ? 1
          : 0,
      required_temp: formData.required_temp || "N/A",

      // ✅ CORREGIDO: Campos de naturaleza (usar números)
      nat_cgo: formData.nat_cgo ? 1 : 0,
      nat_cgo_pieces: formData.nat_cgo_pieces || null,
      nat_cgo_weight: formData.nat_cgo_weight || null,

      nat_dgr: formData.nat_dgr ? 1 : 0,
      nat_dgr_pieces: formData.nat_dgr_pieces || null,
      nat_dgr_weight: formData.nat_dgr_weight || null,

      nat_per: formData.nat_per ? 1 : 0,
      nat_per_pieces: formData.nat_per_pieces || null,
      nat_per_weight: formData.nat_per_weight || null,

      nat_avi: formData.nat_avi ? 1 : 0,
      nat_avi_pieces: formData.nat_avi_pieces || null,
      nat_avi_weight: formData.nat_avi_weight || null,

      nat_val: formData.nat_val ? 1 : 0,
      nat_val_pieces: formData.nat_val_pieces || null,
      nat_val_weight: formData.nat_val_weight || null,

      nat_hum: formData.nat_hum ? 1 : 0,
      nat_hum_pieces: formData.nat_hum_pieces || null,
      nat_hum_weight: formData.nat_hum_weight || null,

      nat_dip: formData.nat_dip ? 1 : 0,
      nat_dip_pieces: formData.nat_dip_pieces || null,
      nat_dip_weight: formData.nat_dip_weight || null,

      nat_nwp: formData.nat_nwp ? 1 : 0,
      nat_nwp_pieces: formData.nat_nwp_pieces || null,
      nat_nwp_weight: formData.nat_nwp_weight || null,

      nat_mal: formData.nat_mal ? 1 : 0,
      nat_mal_pieces: formData.nat_mal_pieces || null,
      nat_mal_weight: formData.nat_mal_weight || null,

      nat_com: formData.nat_com ? 1 : 0,
      nat_com_pieces: formData.nat_com_pieces || null,
      nat_com_weight: formData.nat_com_weight || null,

      nat_vic: formData.nat_vic ? 1 : 0,
      nat_vic_pieces: formData.nat_vic_pieces || null,
      nat_vic_weight: formData.nat_vic_weight || null,

      nat_pil: formData.nat_pil ? 1 : 0,
      nat_pil_pieces: formData.nat_pil_pieces || null,
      nat_pil_weight: formData.nat_pil_weight || null,

      // ✅ CORREGIDO: Campos de manejo (usar números)
      han_ice: formData.han_ice ? 1 : 0,
      han_ice_pieces: formData.han_ice_pieces || null,
      han_ice_weight: formData.han_ice_weight || null,

      han_wet: formData.han_wet ? 1 : 0,
      han_wet_pieces: formData.han_wet_pieces || null,
      han_wet_weight: formData.han_wet_weight || null,

      han_big: formData.han_big ? 1 : 0,
      han_big_pieces: formData.han_big_pieces || null,
      han_big_weight: formData.han_big_weight || null,

      han_hea: formData.han_hea ? 1 : 0,
      han_hea_pieces: formData.han_hea_pieces || null,
      han_hea_weight: formData.han_hea_weight || null,

      han_hwc: formData.han_hwc ? 1 : 0,
      han_hwc_pieces: formData.han_hwc_pieces || null,
      han_hwc_weight: formData.han_hwc_weight || null,

      han_col: formData.han_col ? 1 : 0,
      han_col_pieces: formData.han_col_pieces || null,
      han_col_weight: formData.han_col_weight || null,

      han_fro: formData.han_fro ? 1 : 0,
      han_fro_pieces: formData.han_fro_pieces || null,
      han_fro_weight: formData.han_fro_weight || null,

      han_na: formData.han_na ? 1 : 0,
      han_na_pieces: formData.han_na_pieces || null,
      han_na_weight: formData.han_na_weight || null,

      // ✅ CORREGIDO: Entregas adicionales (usar números/strings según corresponda)
      has_acas:
        formData.has_acas === "1" || formData.has_acas === 1 ? "1" : "0",
      special_cargo: formData.specialLoads ? "1" : "0",
      ata_sat_office:
        formData.ata_sat_office === "1" || formData.ata_sat_office === 1
          ? "1"
          : "0",
      interlineal_cargo: formData.interlineLoads ? "1" : "0",
      intcar_transfer:
        formData.intcar_transfer === "1" || formData.intcar_transfer === 1
          ? "1"
          : "0",
      perishables: formData.nat_per ? "1" : "0",
      perishables_certified:
        formData.perishables_certified === "1" ||
        formData.perishables_certified === 1
          ? "1"
          : "0",
      dangerous_goods: formData.nat_dgr ? "1" : "0",
      dg: formData.dg === "1" || formData.dg === 1 ? "1" : "0",
      live_animals: formData.nat_avi ? "1" : "0",
      shipper_certified:
        formData.shipper_certified === "1" || formData.shipper_certified === 1
          ? "1"
          : "0",
      human_remains: formData.nat_hum ? "1" : "0",
      hr_cert: formData.hr_cert === "1" || formData.hr_cert === 1 ? "1" : "0",
      hr_permission_transfer:
        formData.hr_permission_transfer === "1" ||
        formData.hr_permission_transfer === 1
          ? "1"
          : "0",
      hr_permission: formData.hr_permission === "1" || formData.hr_permission === 1 ? "1" : "0",
      un_dg: formData.un_dg || null,
      has_dg_exempt: formData.has_dg_exempt || null,

      // ✅ CORREGIDO: Contenedores activos
      "c-NoRKN": formData.contActivos_RKN
        ? formData.contActivos_RKN_number
        : null,
      "c-NoRAP": formData.contActivos_RAP
        ? formData.contActivos_RAP_number
        : null,
      "p-NoRKN": formData.prepContenedor_RKN
        ? formData.prepContenedor_RKN_number
        : null,
      "p-NoRAP": formData.prepContenedor_RAP
        ? formData.prepContenedor_RAP_number
        : null,
    };

    console.log("📦 Payload construido:", payload);

    // ✅ Booking flights (mantener igual)
    const boobking_id_col = [];
    const boobking_pieces_col = [];
    const boobking_weight_col = [];
    const boobking_id_fro = [];
    const boobking_pieces_fro = [];
    const boobking_weight_fro = [];

    if (master.booking_flights && master.booking_flights.length > 0) {
      master.booking_flights.forEach((flight, index) => {
        const colPieces = formData[`flights_col_pieces_${index}`];
        const colWeight = formData[`flights_col_weight_${index}`];
        const froPieces = formData[`flights_fro_pieces_${index}`];
        const froWeight = formData[`flights_fro_weight_${index}`];

        if (colPieces !== undefined && colWeight !== undefined) {
          boobking_id_col.push(flight.id);
          boobking_pieces_col.push(colPieces.toString());
          boobking_weight_col.push(colWeight.toString());
        }

        if (froPieces !== undefined && froWeight !== undefined) {
          boobking_id_fro.push(flight.id);
          boobking_pieces_fro.push(froPieces.toString());
          boobking_weight_fro.push(froWeight.toString());
        }
      });
    }

    if (boobking_id_col.length > 0) {
      payload.boobking_id_col = boobking_id_col;
      payload.boobking_pieces_col = boobking_pieces_col;
      payload.boobking_weight_col = boobking_weight_col;
    }

    if (boobking_id_fro.length > 0) {
      payload.boobking_id_fro = boobking_id_fro;
      payload.boobking_pieces_fro = boobking_pieces_fro;
      payload.boobking_weight_fro = boobking_weight_fro;
    }

    console.log("✈️ Booking data:", {
      col: {
        ids: boobking_id_col,
        pieces: boobking_pieces_col,
        weights: boobking_weight_col,
      },
      fro: {
        ids: boobking_id_fro,
        pieces: boobking_pieces_fro,
        weights: boobking_weight_fro,
      },
    });

    // ✅ Enviar al backend
    const response = await axios.post(
      `${process.env.REACT_APP_URL_BACKEND_EXPO}/api/v1/exp_entries/save`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (response.data.code === 1) {
      await Swal.fire({
        title: "Éxito",
        text: response.data.message || "Datos guardados correctamente",
        icon: "success",
        confirmButtonText: "Cerrar",
      });

      return response.data;
    } else {
      throw new Error(response.data.error || "Error al guardar los datos");
    }
  } catch (error) {
    console.error("❌ Error saving reception data:", error);
    console.error("❌ Error response:", error.response?.data);

    Swal.fire({
      title: "Error",
      text:
        error.response?.data?.error ||
        error.message ||
        "Error al guardar los datos",
      icon: "error",
      confirmButtonText: "Aceptar",
    });

    throw error;
  }
};
